import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { CartProvider } from './contexts/CartContext';
import { AuthProvider } from './contexts/AuthContext';
import { KioskProvider, useKiosk } from './contexts/KioskContext';
import Header from './components/Header';
import AdminRoute from './components/AdminRoute';
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import ArtistPage from './pages/ArtistPage';
import ArtistsPage from './pages/ArtistsPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import AccountPage from './pages/AccountPage';
import ApplyArtistPage from './pages/ApplyArtistPage';
import ArtistApprovalPage from './pages/ArtistApprovalPage';
import ArtistGuidePage from './pages/ArtistGuidePage';
import TermsPage from './pages/TermsPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrderList from './pages/admin/AdminOrderList';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import AdminCustomerList from './pages/admin/AdminCustomerList';
import AdminProductList from './pages/admin/AdminProductList';
import AdminProductDetail from './pages/admin/AdminProductDetail';
import AdminArtistList from './pages/admin/AdminArtistList';
import AdminArtistForm from './pages/admin/AdminArtistForm';
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage';
import AdminArtistApplicationsPage from './pages/admin/AdminArtistApplicationsPage';
import AdminArtistApplicationDetail from './pages/admin/AdminArtistApplicationDetail';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminEmailEventsPage from './pages/admin/AdminEmailEventsPage';
import AdminAlliesList from './pages/admin/AdminAlliesList';
import AdminAllyForm from './pages/admin/AdminAllyForm';
import AdminAllyProductForm from './pages/admin/AdminAllyProductForm';
import AdminArtistMetricsPage from './pages/admin/AdminArtistMetricsPage';
import AlliesPage from './pages/AlliesPage';
import AllyPage from './pages/AllyPage';
import FeedbackPage from './pages/FeedbackPage';
import AdminFeedbackPage from './pages/admin/AdminFeedbackPage';
import AdminKioskLoopPage from './pages/admin/AdminKioskLoopPage';
import AdminTtfPiecesPage from './pages/admin/AdminTtfPiecesPage';
import AdminTtfMetricsPage from './pages/admin/AdminTtfMetricsPage';
import TtfOrderPage from './pages/TtfOrderPage';
import MailingConfirmPage from './pages/MailingConfirmPage';
import MailingPreferencesPage from './pages/MailingPreferencesPage';
import AnalyticsTracker from './components/AnalyticsTracker';
import KioskAttractLoop from './components/KioskAttractLoop';
import { useKioskIdle } from './hooks/useKioskIdle';
import useKioskData from './hooks/useKioskData';
import { fetchKioskLoopVideos } from './services/kioskLoopService';
import { CACHE_KEYS } from './services/kioskCache';
import { clarity } from './lib/clarity';
import './App.css';
import './styles/kiosk.css';

// After this long with no touch/mouse/keyboard input, the kiosk falls back
// to the BluppiTV-style attract loop (see KioskAttractLoop.js). Remounting
// KioskAttractLoop resets its playlist index to 0, so this also restarts
// the video loop from the beginning.
const KIOSK_IDLE_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes
// Routes where the attract loop must never interrupt — mid-payment or
// mid-survey is not the moment to steal the screen.
const KIOSK_ATTRACT_DISABLED_ROUTES = ['/checkout', '/feedback'];

const AppShell = () => {
  const { isKiosk, kioskDisplay } = useKiosk();
  const location = useLocation();
  const navigate = useNavigate();
  // The feedback survey is a full-screen, chrome-free page (no header/cart/
  // nav) so it reads cleanly on a kiosk iPad — see src/pages/FeedbackPage.js.
  const isFullBleed = location.pathname === '/feedback';

  // Session-level dimension so kiosk traffic can be filtered out of (or
  // isolated in) Clarity recordings/heatmaps (docs/ui:ux_feature.md §2.2).
  useEffect(() => {
    clarity.setTag('kiosk', String(isKiosk));
    if (kioskDisplay) clarity.setTag('kiosk_display', kioskDisplay);
  }, [isKiosk, kioskDisplay]);

  const attractRouteDisabled = KIOSK_ATTRACT_DISABLED_ROUTES.some((path) =>
    location.pathname.startsWith(path)
  );
  // Only fetches (and only caches offline) while there's a chance the loop
  // will actually show — no point costing every non-kiosk visitor a request.
  const { data: loopPlaylist } = useKioskData(CACHE_KEYS.loopVideos, fetchKioskLoopVideos, {
    enabled: isKiosk,
  });
  const attractEnabled =
    isKiosk && !attractRouteDisabled && (loopPlaylist?.length ?? 0) > 0;
  const isAttractIdle = useKioskIdle(KIOSK_IDLE_TIMEOUT_MS, attractEnabled);

  return (
    <div className={`App${isKiosk ? ' kiosk-mode' : ''}${kioskDisplay ? ` kiosk-display-${kioskDisplay}` : ''}`}>
      <AnalyticsTracker />
      {!isFullBleed && <Header />}
      <main className={isFullBleed ? 'main--full-bleed' : undefined}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/product/:identifier" element={<ProductPage />} />
          <Route path="/artists" element={<ArtistsPage />} />
          <Route path="/artist/:identifier" element={<ArtistPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/pedidos/taipei-toy-festival" element={<TtfOrderPage />} />
          <Route path="/account/apply-artist" element={<ApplyArtistPage />} />
          <Route path="/artist-guide" element={<ArtistGuidePage />} />
          <Route path="/artist-approval" element={<ArtistApprovalPage />} />
          <Route path="/allies" element={<AlliesPage />} />
          <Route path="/ally/:identifier" element={<AllyPage />} />
          <Route path="/terminos-y-condiciones" element={<TermsPage />} />
          <Route path="/correos/confirmar" element={<MailingConfirmPage />} />
          <Route path="/correos/preferencias" element={<MailingPreferencesPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/orders" element={<AdminRoute><AdminOrderList /></AdminRoute>} />
          <Route path="/admin/orders/:id" element={<AdminRoute><AdminOrderDetail /></AdminRoute>} />
          <Route path="/admin/customers" element={<AdminRoute><AdminCustomerList /></AdminRoute>} />
          <Route path="/admin/products" element={<AdminRoute><AdminProductList /></AdminRoute>} />
          <Route path="/admin/products/:id" element={<AdminRoute><AdminProductDetail /></AdminRoute>} />
          <Route path="/admin/artists" element={<AdminRoute><AdminArtistList /></AdminRoute>} />
          <Route path="/admin/artists/:id" element={<AdminRoute><AdminArtistForm /></AdminRoute>} />
          <Route path="/admin/notifications" element={<AdminRoute><AdminNotificationsPage /></AdminRoute>} />
          <Route path="/admin/artist-applications" element={<AdminRoute><AdminArtistApplicationsPage /></AdminRoute>} />
          <Route path="/admin/artist-applications/:id" element={<AdminRoute><AdminArtistApplicationDetail /></AdminRoute>} />
          <Route path="/admin/payments" element={<AdminRoute><AdminPaymentsPage /></AdminRoute>} />
          <Route path="/admin/email-events" element={<AdminRoute><AdminEmailEventsPage /></AdminRoute>} />
          <Route path="/admin/artist-metrics" element={<AdminRoute><AdminArtistMetricsPage /></AdminRoute>} />
          <Route path="/admin/allies" element={<AdminRoute><AdminAlliesList /></AdminRoute>} />
          <Route path="/admin/allies/new" element={<AdminRoute><AdminAllyForm /></AdminRoute>} />
          <Route path="/admin/allies/:id" element={<AdminRoute><AdminAllyForm /></AdminRoute>} />
          <Route path="/admin/allies/:id/products/new" element={<AdminRoute><AdminAllyProductForm /></AdminRoute>} />
          <Route path="/admin/allies/:id/products/:productId" element={<AdminRoute><AdminAllyProductForm /></AdminRoute>} />
          <Route path="/admin/feedback" element={<AdminRoute><AdminFeedbackPage /></AdminRoute>} />
          <Route path="/admin/kiosk-loop" element={<AdminRoute><AdminKioskLoopPage /></AdminRoute>} />
          <Route path="/admin/ttf-pieces" element={<AdminRoute><AdminTtfPiecesPage /></AdminRoute>} />
          <Route path="/admin/ttf-metrics" element={<AdminRoute><AdminTtfMetricsPage /></AdminRoute>} />
        </Routes>
      </main>
      {attractEnabled && isAttractIdle && (
        <KioskAttractLoop playlist={loopPlaylist} onDismiss={() => navigate('/artists')} />
      )}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <KioskProvider>
          <Router>
            <AppShell />
          </Router>
        </KioskProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
